import React from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Grid,
  Divider,
  IconButton,
  Icon,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

const modalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", md: "800px" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "8px",
  overflowY: "auto",
};

function DetallesCotizacion({ open, onClose, cotizacion }) {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  if (!cotizacion) return null;

  const getEstatusColor = (estatus) => {
    switch (estatus) {
      case "vigente":
        return "info";
      case "aceptada":
        return "success";
      case "rechazada":
        return "error";
      case "vencida":
        return "warning";
      default:
        return "default";
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(value || 0);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyles}>
        {/* Header */}
        <MDBox
          variant="gradient"
          bgColor={sidenavColor}
          borderRadius="8px 8px 0 0"
          p={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <MDBox>
            <MDTypography variant="h5" color="white" fontWeight="medium">
              Detalles de Cotización
            </MDTypography>
            <MDTypography variant="button" color="white" fontWeight="regular">
              Folio: {cotizacion.folio || "N/A"}
            </MDTypography>
          </MDBox>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </MDBox>

        <MDBox p={3}>
          <Grid container spacing={3}>
            {/* Resumen Principal */}
            <Grid item xs={12}>
              <Card sx={{ bgcolor: "#f8f9fa", boxShadow: "none", border: "1px solid #e0e0e0" }}>
                <MDBox p={2} display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      textTransform="uppercase"
                    >
                      Estatus
                    </MDTypography>
                    <MDBox mt={0.5}>
                      <Chip
                        label={cotizacion.estatus}
                        color={getEstatusColor(cotizacion.estatus)}
                        size="small"
                      />
                    </MDBox>
                  </MDBox>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      textTransform="uppercase"
                    >
                      Fecha Cotización
                    </MDTypography>
                    <MDTypography variant="h6">
                      {new Date(cotizacion.fecha_cotizacion).toLocaleDateString("es-MX")}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      textTransform="uppercase"
                    >
                      Vigencia hasta
                    </MDTypography>
                    <MDTypography variant="h6">
                      {new Date(cotizacion.fecha_vigencia).toLocaleDateString("es-MX")}
                    </MDTypography>
                  </MDBox>
                  <MDBox textAlign="right">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontWeight="bold"
                      textTransform="uppercase"
                    >
                      Total
                    </MDTypography>
                    <MDTypography variant="h5" color={sidenavColor} fontWeight="bold">
                      {formatCurrency(cotizacion.total)}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>

            {/* Información del Proveedor */}
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Proveedor</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MDBox>
                    <MDTypography variant="button" color="text" fontWeight="bold">
                      Nombre:{" "}
                    </MDTypography>
                    <MDTypography variant="button">
                      {cotizacion.proveedor?.nombre || "N/A"}
                    </MDTypography>
                  </MDBox>
                  <MDBox mt={1}>
                    <MDTypography variant="button" color="text" fontWeight="bold">
                      RFC:{" "}
                    </MDTypography>
                    <MDTypography variant="button">
                      {cotizacion.proveedor?.rfc || "N/A"}
                    </MDTypography>
                  </MDBox>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Detalles de Productos */}
            <Grid item xs={12}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Productos / Servicios Cotizados</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow>
                          <TableCell>Producto</TableCell>
                          <TableCell align="center">Cantidad</TableCell>
                          <TableCell align="right">Precio Unit.</TableCell>
                          <TableCell align="right">Subtotal</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cotizacion.detalles && cotizacion.detalles.length > 0 ? (
                          cotizacion.detalles.map((detalle, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {detalle.producto?.nombre || "Producto desconocido"}
                              </TableCell>
                              <TableCell align="center">{detalle.cantidad}</TableCell>
                              <TableCell align="right">
                                {formatCurrency(detalle.precio_unitario)}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(detalle.subtotal)}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              No hay items registrados
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell colSpan={2} />
                          <TableCell align="right">
                            <strong>Subtotal</strong>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(cotizacion.subtotal)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={2} />
                          <TableCell align="right">
                            <strong>Impuestos</strong>
                          </TableCell>
                          <TableCell align="right">{formatCurrency(cotizacion.impuesto)}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={2} />
                          <TableCell align="right">
                            <strong>TOTAL</strong>
                          </TableCell>
                          <TableCell align="right">
                            <MDTypography variant="h6" color={sidenavColor} fontWeight="bold">
                              {formatCurrency(cotizacion.total)}
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Notas */}
            {cotizacion.notas && (
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Notas / Observaciones</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MDTypography variant="button" color="text">
                      {cotizacion.notas}
                    </MDTypography>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )}
          </Grid>
        </MDBox>

        {/* Footer Actions */}
        <MDBox p={3} display="flex" justifyContent="flex-end">
          <MDButton variant="outlined" color="secondary" onClick={onClose}>
            Cerrar
          </MDButton>
        </MDBox>
      </Box>
    </Modal>
  );
}

DetallesCotizacion.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  cotizacion: PropTypes.object,
};

export default DetallesCotizacion;
